import { useCallback, useEffect, useState } from "react";

const INTEGRATIONS = [
  { name: "Stripe",          desc: "Payment processing and revenue tracking", configured: false },
  { name: "CF Browser Run",  desc: "Headless browser for audit scans",        configured: true  },
  { name: "Pharow",          desc: "French B2B prospect data",                configured: false },
  { name: "PageSpeed Insights", desc: "Core Web Vitals + Lighthouse",         configured: true  },
];

// Mirrors src/config/inboxes.yaml. Apex levier-ia.fr stays on Google
// Workspace for contact@; only agents.levier-ia.fr is on Cloudflare
// Email Routing. Verifier + Integrator are internal, no inbox.
const INBOXES = [
  { email: "support@agents.levier-ia.fr",     agent: "Support Agent"     },
  { email: "marketing@agents.levier-ia.fr",   agent: "Marketing Agent"   },
  { email: "growth@agents.levier-ia.fr",      agent: "Growth Agent"      },
  { email: "fundraising@agents.levier-ia.fr", agent: "Fundraising Agent" },
  { email: "ceo@agents.levier-ia.fr",         agent: "CEO Agent"         },
  { email: "engineering@agents.levier-ia.fr", agent: "Engineering Agent" },
];

export function Settings() {
  return (
    <div className="space-y-8 lg:space-y-10">
      <header className="reveal">
        <p className="eyebrow">Console · 04</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">System </span>
          <span className="text-accent-gradient">Configuration</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Integrations, inbox routing, and runtime behavior controls for the swarm.
        </p>
      </header>

      {/* Integrations */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Integrations</p>
        </div>

        <div className="panel reveal p-4 sm:p-6" style={{ animationDelay: "80ms" }}>
          <div className="space-y-2">
            {INTEGRATIONS.map((integration, i) => (
              <div
                key={integration.name}
                className="reveal grid grid-cols-[1fr_auto] gap-4 items-center px-4 py-4 rounded-lg border border-[var(--color-border)]/60 bg-[var(--color-bg-elev)]/40 hover:border-[var(--color-border-strong)] transition-colors"
                style={{ animationDelay: `${120 + i * 50}ms` }}
              >
                <div>
                  <div className="flex items-baseline gap-2">
                    <p className="font-display italic text-xl text-[var(--color-text)]">
                      {integration.name}
                    </p>
                    <span className="font-mono text-[10px] text-[var(--color-text-dim)] tracking-wider uppercase">
                      · int.{String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">{integration.desc}</p>
                </div>

                {integration.configured ? (
                  <span className="pill text-[var(--color-status-active)] border-[rgba(132,204,22,0.4)] bg-[rgba(132,204,22,0.08)]">
                    <span className="live-dot text-[var(--color-status-active)]" />
                    Configured
                  </span>
                ) : (
                  <span className="pill text-[var(--color-text-dim)] border-[var(--color-border)] bg-[var(--color-bg-elev)]">
                    Not Set
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inboxes */}
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Inbox Routing</p>
        </div>

        <div className="panel reveal p-6" style={{ animationDelay: "200ms" }}>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-4">
            Source: config/inboxes.yaml
          </p>
          <div className="space-y-1.5">
            {INBOXES.map((inbox, i) => (
              <div
                key={inbox.email}
                className="reveal grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 rounded-lg border border-[var(--color-border)]/50 bg-[var(--color-bg-elev)]/40 hover:border-[var(--color-accent)]/40 transition-colors group"
                style={{ animationDelay: `${260 + i * 40}ms` }}
              >
                <span className="font-mono text-[10px] text-[var(--color-text-dim)] tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <code className="font-mono text-sm text-[var(--color-accent-bright)] group-hover:text-[var(--color-accent-glow)] transition-colors truncate">
                  {inbox.email}
                </code>
                <span className="font-mono text-[11px] tracking-wider text-[var(--color-text-muted)]">
                  → {inbox.agent}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AgentModelsSection />
    </div>
  );
}

// ─── Agent models picker ────────────────────────────────────────────────────

interface CuratedModelView {
  id: string;
  label: string;
  inPer1M: number;
  outPer1M: number;
  ctx: string;
  note: string;
}

interface AgentModelRow {
  agentId: string;
  override?: { id: string; temperature?: number; maxTokens?: number; fallback?: string };
}

interface AgentsModelsPayload {
  agents: AgentModelRow[];
  default: { id: string; temperature: number; maxTokens: number };
  curated: CuratedModelView[];
}

interface OpenRouterModelLite {
  id: string;
  name: string;
  pricing?: { prompt?: string; completion?: string };
  context_length?: number;
}

/** Pricing string from OpenRouter is per-token in USD. Convert to per-1M. */
function perMillion(perToken: string | undefined): number | undefined {
  if (!perToken) return undefined;
  const n = parseFloat(perToken);
  if (!Number.isFinite(n)) return undefined;
  return n * 1_000_000;
}

function formatPrice(n: number | undefined): string {
  if (n === undefined) return "—";
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(3)}`;
}

function AgentModelsSection() {
  const [data, setData] = useState<AgentsModelsPayload | null>(null);
  const [livePrices, setLivePrices] = useState<Map<string, OpenRouterModelLite>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingAgent, setSavingAgent] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [agentsRes, orRes] = await Promise.all([
        fetch("/api/agents/models"),
        fetch("/api/openrouter/models").catch(() => null),
      ]);
      if (!agentsRes.ok) throw new Error(`agents/models HTTP ${agentsRes.status}`);
      const agents = (await agentsRes.json()) as AgentsModelsPayload;
      setData(agents);
      if (orRes && orRes.ok) {
        const or = (await orRes.json()) as { data: OpenRouterModelLite[] };
        setLivePrices(new Map(or.data.map((m) => [m.id, m])));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setModel = useCallback(
    async (agentId: string, modelId: string | null, fallback?: string | null) => {
      setSavingAgent(agentId);
      try {
        const body: { agentId: string; model: null | { id: string; fallback?: string; maxTokens: number } } = {
          agentId,
          model: modelId
            ? { id: modelId, maxTokens: 16384, ...(fallback ? { fallback } : {}) }
            : null,
        };
        const res = await fetch("/api/agents/models", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(await res.text());
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setSavingAgent(null);
      }
    },
    [load],
  );

  if (loading && !data) {
    return (
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Agent Models</p>
        </div>
        <div className="panel p-4 sm:p-6 text-[var(--color-text-dim)]">
          <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Loading…</span>
        </div>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="space-y-4">
        <div className="section-rule">
          <p className="eyebrow">// Agent Models</p>
        </div>
        <div className="panel p-4 sm:p-6 text-[var(--color-status-error)] text-sm">
          {error}
        </div>
      </section>
    );
  }
  if (!data) return null;

  // Merge curated + live prices: live OpenRouter wins when both are present.
  const mergedCurated = data.curated.map((c) => {
    const live = livePrices.get(c.id);
    return {
      ...c,
      livePromptPer1M: live ? perMillion(live.pricing?.prompt) : undefined,
      liveCompletionPer1M: live ? perMillion(live.pricing?.completion) : undefined,
      liveContextLength: live?.context_length,
    };
  });

  return (
    <section className="space-y-4">
      <div className="section-rule">
        <p className="eyebrow">// Agent Models</p>
      </div>

      <div className="panel reveal p-6" style={{ animationDelay: "320ms" }}>
        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] mb-4">
          Per-agent override · default = <span className="text-[var(--color-accent-bright)]">{data.default.id}</span> · maxTokens fixed at {data.default.maxTokens}
        </p>

        <div className="space-y-2">
          {data.agents.map((agent) => (
            <AgentRow
              key={agent.agentId}
              agent={agent}
              curated={mergedCurated}
              defaultModelId={data.default.id}
              saving={savingAgent === agent.agentId}
              onSet={(modelId, fallback) => setModel(agent.agentId, modelId, fallback)}
            />
          ))}
        </div>

        {error && <p className="mt-4 text-sm text-[var(--color-status-error)]">{error}</p>}
      </div>
    </section>
  );
}

interface AgentRowProps {
  agent: AgentModelRow;
  curated: Array<CuratedModelView & {
    livePromptPer1M?: number;
    liveCompletionPer1M?: number;
    liveContextLength?: number;
  }>;
  defaultModelId: string;
  saving: boolean;
  onSet: (modelId: string | null, fallback: string | null) => void;
}

function AgentRow({ agent, curated, defaultModelId, saving, onSet }: AgentRowProps) {
  const currentId = agent.override?.id ?? defaultModelId;
  const currentFallback = agent.override?.fallback ?? "";
  const isOverride = !!agent.override;

  // The current model might not be in the curated list (e.g. user typed
  // a custom ID via the API). Inject it so the <select> stays consistent.
  const inCurated = curated.some((c) => c.id === currentId);
  const options = inCurated
    ? curated
    : [{ id: currentId, label: currentId, inPer1M: 0, outPer1M: 0, ctx: "?", note: "Custom" }, ...curated];

  return (
    <div
      className="grid grid-cols-[1fr_auto] gap-4 items-center px-4 py-3 rounded-lg border border-[var(--color-border)]/60 bg-[var(--color-bg-elev)]/40"
    >
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <p className="font-display italic text-lg text-[var(--color-text)] capitalize">
            {agent.agentId}
          </p>
          {isOverride && (
            <span className="pill text-[var(--color-accent-bright)] border-[var(--color-accent)]/40 bg-[var(--color-accent)]/8">
              override
            </span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={currentId}
            disabled={saving}
            onChange={(e) => onSet(e.target.value, currentFallback || null)}
            className="font-mono text-xs px-3 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            style={{
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              border: "1px solid var(--color-border)",
              minWidth: "260px",
            }}
          >
            {options.map((m) => {
              const inPrice = m.livePromptPer1M ?? m.inPer1M;
              const outPrice = m.liveCompletionPer1M ?? m.outPer1M;
              return (
                <option key={m.id} value={m.id}>
                  {m.label} — {formatPrice(inPrice)} in / {formatPrice(outPrice)} out · {m.ctx}
                </option>
              );
            })}
          </select>

          <span className="font-mono text-[10px] text-[var(--color-text-dim)] uppercase tracking-wider">
            fallback
          </span>
          <select
            value={currentFallback}
            disabled={saving}
            onChange={(e) => onSet(currentId, e.target.value || null)}
            className="font-mono text-xs px-3 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            style={{
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              border: "1px solid var(--color-border)",
              minWidth: "200px",
            }}
          >
            <option value="">(none)</option>
            {curated.filter((m) => m.id !== currentId).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {saving && (
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)] blink">
            saving…
          </span>
        )}
        {isOverride && !saving && (
          <button
            type="button"
            onClick={() => onSet(null, null)}
            className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-colors"
            style={{
              background: "var(--color-bg-card)",
              color: "var(--color-text-muted)",
              border: "1px solid var(--color-border)",
            }}
            title="Reset to default"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
